import { compact, flatten, partial, uniq } from '@quromedical/utils';
import type {
  CareTeam,
  Device,
  Group,
  GroupMember,
  HealthcareService,
  Location,
  Organization,
  Patient,
  Practitioner,
  PractitionerRole,
  RelatedPerson,
  Specimen,
} from 'fhir/r4';
import { valsOrNone } from '../array';
import { GroupSecurity } from '../coding';
import type { WithId } from '../types';
import { createReference, getReferenceData, getReferenceId } from './id';
import type { CreateReference } from './reference';

const securityCodes = Object.values(GroupSecurity);

/**
 * Get all `GroupSecurity` codes from a group
 * @param group
 * @returns a unique list of `GroupSecurity`[]
 */
const getGroupSecurityCodes = (group: Group): GroupSecurity[] => {
  const coding = group.code?.coding || [];
  const codes = coding.map((c) => c.code);

  const filteredCodes = compact(codes).filter((c) => {
    const isSecurityCode = securityCodes.includes(c as GroupSecurity);
    return isSecurityCode;
  }) as GroupSecurity[];

  return filteredCodes;
};

/**
 * Get all `GroupSecurity` codes for many groups
 * @param group
 * @returns a unique list of `GroupSecurity`[]
 */
export const getJointGroupSecurityCodes = (
  groups: Group[],
): GroupSecurity[] => {
  const codes = groups.map(getGroupSecurityCodes);

  const flatCodes = flatten(codes);

  return uniq(flatCodes);
};

/**
 * Updates status of group members. Adding new members and updating any members where required
 * @param membersToUpdate members to add or update
 * @param group existing group
 * @returns group with member modifications implemented
 */
export const updateGroupMembers = (
  membersToUpdate: GroupMember[],
  group: Group,
): Group => {
  const removeUnchangedMembers = (member: GroupMember): boolean => {
    const foundMember = membersToUpdate.find(
      (u) =>
        u?.entity?.reference &&
        u.entity?.reference === member?.entity?.reference,
    );
    return !foundMember;
  };

  const baseMembers = group.member || [];
  const unchangedMembers = baseMembers.filter(removeUnchangedMembers);
  const updatedMembers = unchangedMembers.concat(membersToUpdate);
  const updatedGroup: Group = {
    ...group,
    member: valsOrNone(updatedMembers),
  };

  return updatedGroup;
};

/**
 * Get the organizaiton from a list of groups
 */
export const getOrganizationIds = (groups: Group[]): string[] => {
  const orgIds = groups.map((group) => getReferenceId(group.managingEntity));

  return compact(orgIds);
};

/**
 * Is the Group Active
 */
export const isGroupActive = (group: Group): boolean => !!group.active;

export const getActiveGroupMembers = (
  group: Group,
): GroupMember[] | undefined => {
  return group.member?.filter((m) => !m.inactive);
};
/**
 * creates a function that checks if that provided practitioner is an active member of the group
 * @param practitionerId
 * @returns
 */
export const isActiveGroupMember = (
  practitionerId: string,
  group: Group,
): boolean => {
  const { member } = group;

  if (!member?.length) {
    return false;
  }

  const activeMembers = member.filter((m) => !m.inactive);

  const foundEntity = activeMembers.find((m) => {
    const ref = getReferenceData(m.entity);

    if (ref?.resourceType !== 'Practitioner') {
      return false;
    }

    return ref.id === practitionerId;
  });

  return !!foundEntity;
};

const getGroupCodes = (group: fhir4.Group): GroupSecurity[] => {
  const coding = group.code?.coding || [];
  const codes = coding.map((c) => c.code);

  const filteredCodes = compact(codes).filter((c) => {
    const hasCode = securityCodes.includes(c as GroupSecurity);
    return hasCode;
  }) as GroupSecurity[];

  return filteredCodes;
};

const joinCodes = (groups: fhir4.Group[]): GroupSecurity[] => {
  const codes = groups.map(getGroupCodes);

  const flatCodes = flatten(codes);

  return uniq(flatCodes);
};

/**
 * Gets that codes for all groups that are: `active` and has the practitioner as an `active member`
 * @param groups
 * @param practitionerId
 * @returns
 */
export const getUserActiveGroupCodes = (
  groups: fhir4.Group[],
  practitionerId: string,
) => {
  const activeGroups = groups.filter(isGroupActive);

  const practitionerGroups = activeGroups.filter((g) =>
    isActiveGroupMember(practitionerId, g),
  );

  return joinCodes(practitionerGroups);
};

/**
 * Create a filter that can be used to get groups where a practitioner is an active member
 *
 * @example
 * const isActiveMember = isPractitionerMemberActive(practitionerId)
 * const activeGroups = groups.filter(isGroupActive).filter(isActiveMember)
 */
export const isPractitionerMemberActive = (practitionerId: string) =>
  partial(isActiveGroupMember, practitionerId);

export const getGroupReferences = (
  userGroups: WithId<fhir4.Group>[],
): CreateReference[] =>
  userGroups.map((group) => createReference('Group', group.id, group.name));

type GroupMemberEntity =
  | CareTeam
  | Group
  | Device
  | Patient
  | Practitioner
  | PractitionerRole
  | HealthcareService
  | Organization
  | Location
  | Specimen
  | RelatedPerson;

export function createGroupMemberEntity<
  TResourceType extends GroupMemberEntity['resourceType'],
>(resourceType: TResourceType, id: string): GroupMember {
  return {
    entity: createReference(resourceType, id),
  };
}

export function createGroupMemberEntityFromResource<
  TResource extends GroupMemberEntity,
>(resource: WithId<TResource>): GroupMember {
  return createGroupMemberEntity(resource.resourceType, resource.id);
}
